import * as React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Field } from './Field';
import { Icon } from './Icon';
import { matchesSearch } from './selectFilter';

export type SelectOption = { label: string; value: string };
export type SelectGroup = { label: string; options: SelectOption[] };

type Props = {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[] | SelectGroup[];

  // Variant
  searchable?: boolean;

  // Form copy — defaults defined inline
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsLabel?: string;

  requirement?: 'required' | 'optional';
  hint?: string;
  error?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
};

// Discriminate flat vs grouped by checking the first item.
function isGrouped(opts: SelectOption[] | SelectGroup[]): opts is SelectGroup[] {
  return opts.length > 0 && Array.isArray((opts[0] as SelectGroup).options);
}

function flatten(opts: SelectOption[] | SelectGroup[]): SelectOption[] {
  return isGrouped(opts) ? opts.flatMap((g) => g.options) : opts;
}

const TYPEAHEAD_RESET_MS = 500;

export function Select({
  label,
  value,
  onChange,
  options,
  searchable = false,
  placeholder,
  searchPlaceholder,
  noResultsLabel,
  requirement,
  hint,
  error,
  disabled,
  id,
  name,
}: Props) {
  // Search-input placeholder + empty-state copy default to the active locale.
  // Callers may still override per-call via the props above.
  const { t } = useTranslation('ui');
  const searchPlaceholderResolved = searchPlaceholder ?? t('searchPlaceholder');
  const noResultsLabelResolved = noResultsLabel ?? t('noMatches');
  const reactId = React.useId();
  const fieldId = id ?? reactId;
  const listboxId = `${fieldId}-listbox`;

  const [open, setOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(-1);
  const [query, setQuery] = React.useState('');

  const POPUP_MAX_HEIGHT = 320;
  const POPUP_GAP = 4;
  // The popup is portaled to <body> and positioned `fixed` so it escapes the
  // modal's `overflow-hidden` panel + scroll container (otherwise the footer
  // clips/covers it). Coords are measured from the trigger's viewport rect.
  const [popupStyle, setPopupStyle] = React.useState<React.CSSProperties>({});

  const rootRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const popupRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const typeaheadRef = React.useRef<string>('');
  const typeaheadTimerRef = React.useRef<number | undefined>(undefined);

  const flat = React.useMemo(() => flatten(options), [options]);
  const selectedIndex = flat.findIndex((o) => o.value === value);
  const selectedLabel = selectedIndex >= 0 ? flat[selectedIndex].label : '';

  const visibleFlat = React.useMemo<{ option: SelectOption; flatIndex: number }[]>(() => {
    if (!searchable) return flat.map((option, flatIndex) => ({ option, flatIndex }));
    return flat
      .map((option, flatIndex) => ({ option, flatIndex }))
      .filter(({ option }) => matchesSearch(option.label, query));
  }, [searchable, flat, query]);

  const visibleHighlightPos = visibleFlat.findIndex(
    (e) => e.flatIndex === highlightedIndex,
  );

  const openPopup = React.useCallback(() => {
    if (disabled) return;
    if (searchable) {
      setQuery('');
      setHighlightedIndex(flat[0] ? 0 : -1);
    } else {
      setHighlightedIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
    setOpen(true);
  }, [disabled, searchable, flat, selectedIndex]);

  const closePopup = React.useCallback((focusTrigger = true) => {
    setOpen(false);
    setHighlightedIndex(-1);
    setQuery('');
    typeaheadRef.current = '';
    if (focusTrigger) {
      window.requestAnimationFrame(() => triggerRef.current?.focus());
    }
  }, []);

  const commit = React.useCallback(
    (index: number) => {
      const option = flat[index];
      if (!option) return;
      onChange(option.value);
      closePopup();
    },
    [flat, onChange, closePopup],
  );

  // Reset highlight on query change while open + searchable
  React.useEffect(() => {
    if (!open || !searchable) return;
    setHighlightedIndex(visibleFlat[0]?.flatIndex ?? -1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open, searchable]);

  // Focus the search input on open
  React.useEffect(() => {
    if (open && searchable) {
      window.requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  }, [open, searchable]);

  React.useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      // The popup lives in a portal outside rootRef, so check it separately.
      if (!rootRef.current?.contains(target) && !popupRef.current?.contains(target)) {
        closePopup(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, closePopup]);

  React.useEffect(
    () => () => {
      if (typeaheadTimerRef.current !== undefined) {
        window.clearTimeout(typeaheadTimerRef.current);
      }
    },
    [],
  );

  React.useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const flipUp =
        spaceBelow < POPUP_MAX_HEIGHT + POPUP_GAP && spaceAbove > spaceBelow;
      // Clamp height to the available side so the popup never spills off-screen.
      const available = (flipUp ? spaceAbove : spaceBelow) - POPUP_GAP;
      const maxHeight = Math.max(0, Math.min(POPUP_MAX_HEIGHT, available));
      setPopupStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        maxHeight,
        ...(flipUp
          ? { bottom: window.innerHeight - rect.top + POPUP_GAP }
          : { top: rect.bottom + POPUP_GAP }),
      });
    };
    measure();
    window.addEventListener('resize', measure);
    // Capture phase so the modal's inner scroll container re-anchors the popup too.
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  const handleTypeahead = (key: string) => {
    if (typeaheadTimerRef.current !== undefined) {
      window.clearTimeout(typeaheadTimerRef.current);
    }
    typeaheadRef.current = (typeaheadRef.current + key).toLowerCase();
    const buf = typeaheadRef.current;
    typeaheadTimerRef.current = window.setTimeout(() => {
      typeaheadRef.current = '';
    }, TYPEAHEAD_RESET_MS);

    const match = flat.findIndex((o) => o.label.toLowerCase().startsWith(buf));
    if (match !== -1) setHighlightedIndex(match);
  };

  const onTriggerKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!open) {
      if (
        e.key === ' ' ||
        e.key === 'Enter' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowUp'
      ) {
        e.preventDefault();
        openPopup();
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((i) => (i + 1) % flat.length);
        return;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((i) => (i - 1 + flat.length) % flat.length);
        return;
      case 'Home':
        e.preventDefault();
        setHighlightedIndex(0);
        return;
      case 'End':
        e.preventDefault();
        setHighlightedIndex(flat.length - 1);
        return;
      case 'Enter':
      case ' ':
        e.preventDefault();
        commit(highlightedIndex);
        return;
      case 'Escape':
        e.preventDefault();
        closePopup();
        return;
      case 'Tab':
        closePopup(false);
        return;
      default:
        if (e.key.length === 1 && /\S/.test(e.key)) {
          e.preventDefault();
          handleTypeahead(e.key);
        }
    }
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        if (visibleFlat.length === 0) return;
        const next = (visibleHighlightPos + 1) % visibleFlat.length;
        setHighlightedIndex(visibleFlat[next].flatIndex);
        return;
      }
      case 'ArrowUp': {
        e.preventDefault();
        if (visibleFlat.length === 0) return;
        const prev = (visibleHighlightPos - 1 + visibleFlat.length) % visibleFlat.length;
        setHighlightedIndex(visibleFlat[prev].flatIndex);
        return;
      }
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) commit(highlightedIndex);
        return;
      case 'Escape':
        e.preventDefault();
        if (query !== '') setQuery('');
        else closePopup();
        return;
      case 'Tab':
        closePopup(false);
        return;
    }
  };

  const optionId = (i: number) => `${listboxId}-option-${i}`;

  const triggerStateCls = disabled
    ? 'bg-[color:var(--neutral-100)] text-primary/50 cursor-not-allowed border-primary/15'
    : error
      ? 'border-error pl-[19px] shadow-[inset_3px_0_0_var(--error)]'
      : open
        ? 'border-primary'
        : 'border-primary/15 hover:border-primary/50 focus-visible:border-primary';

  const renderOption = (option: SelectOption, i: number) => {
    const isSelected = option.value === value;
    const isHighlighted = i === highlightedIndex;
    return (
      <li
        key={option.value}
        id={optionId(i)}
        role="option"
        aria-selected={isSelected}
        onMouseEnter={() => setHighlightedIndex(i)}
        onClick={() => commit(i)}
        className={`flex cursor-pointer items-center justify-between px-4 py-2.5 text-[15px] leading-snug text-primary ${
          isHighlighted ? 'bg-primary/[0.06]' : ''
        } ${isSelected ? 'font-medium' : ''}`}
      >
        <span className="block truncate">{option.label}</span>
        {isSelected && (
          <span className="ml-3 inline-flex text-[14px] text-primary">
            <Icon name="check" />
          </span>
        )}
      </li>
    );
  };

  return (
    <Field label={label} requirement={requirement} hint={hint} error={error} htmlFor={fieldId}>
      <div ref={rootRef} className="relative w-full">
        <button
          ref={triggerRef}
          id={fieldId}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-activedescendant={
            open && highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined
          }
          aria-invalid={error && !disabled ? true : undefined}
          disabled={disabled}
          onClick={() => (open ? closePopup() : openPopup())}
          onKeyDown={onTriggerKeyDown}
          className={`flex w-full items-center justify-between border bg-white py-3.5 pl-4 pr-11 text-left font-sans text-[15px] font-normal normal-case leading-snug tracking-body text-primary outline-none transition-[border-color] duration-200 ${triggerStateCls}`}
        >
          <span
            className={`block truncate ${selectedLabel ? '' : 'text-primary/50'}`}
          >
            {selectedLabel || placeholder || ' '}
          </span>
          {name ? <input type="hidden" name={name} value={value} /> : null}
        </button>
        <span
          aria-hidden="true"
          className={`pointer-events-none absolute right-[18px] top-1/2 -translate-y-1/2 text-[14px] text-primary transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        >
          <Icon name="chevron-down" />
        </span>

        {open && createPortal(
          <div
            ref={popupRef}
            style={popupStyle}
            className="pointer-events-auto z-[200] font-sans font-normal normal-case tracking-body overflow-y-auto border border-primary/15 bg-white shadow-[var(--shadow-card)]"
          >
            {searchable && (
              <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-primary/10 bg-white px-4 py-2.5">
                <span className="text-[15px] text-primary/60">
                  <Icon name="search" />
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  role="searchbox"
                  aria-autocomplete="list"
                  aria-controls={listboxId}
                  aria-activedescendant={
                    highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined
                  }
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onSearchKeyDown}
                  placeholder={searchPlaceholderResolved}
                  className="w-full bg-transparent text-[16px] md:text-[15px] leading-snug text-primary outline-none placeholder:text-primary/40"
                />
              </div>
            )}
            {visibleFlat.length === 0 ? (
              <div className="px-4 py-6 text-center text-[14px] text-primary/50">
                {noResultsLabelResolved}
              </div>
            ) : (
              <ul
                id={listboxId}
                role="listbox"
                aria-label={label}
                className="py-1"
              >
                {searchable
                  ? visibleFlat.map(({ option, flatIndex }) => renderOption(option, flatIndex))
                  : isGrouped(options)
                    ? (() => {
                        let flatIndex = 0;
                        return options.map((group, gi) => {
                          const groupHeadingId = `${listboxId}-group-${gi}`;
                          return (
                            <li key={`group-${gi}`} role="group" aria-labelledby={groupHeadingId}>
                              <div
                                id={groupHeadingId}
                                className="px-4 pb-1.5 pt-3 text-[10px] font-medium uppercase tracking-link text-primary/65"
                              >
                                {group.label}
                              </div>
                              <ul role="presentation">
                                {group.options.map((option) => renderOption(option, flatIndex++))}
                              </ul>
                            </li>
                          );
                        });
                      })()
                    : flat.map((option, i) => renderOption(option, i))}
              </ul>
            )}
          </div>,
          document.body,
        )}
      </div>
    </Field>
  );
}
